import { Component, computed, input, model, output } from '@angular/core'
import { IdrColDef, IdrColumnType } from './idr-columns'
import { MatFormFieldModule } from '@angular/material/form-field'
import { FormsModule } from '@angular/forms'
import { MatInputModule } from '@angular/material/input'
import { sumBy } from 'lodash'
import { IdrColumnsSelectorGridComponent } from './idr-columns-selector-grid.component'
import { SearchFieldComponent } from '../shared/components/search-field/search-field.component'

export type ColumnSelection = Readonly<{
  id: string
  label: string
  columnType: IdrColumnType
  selected: boolean
  colDefs: readonly IdrColDef[]
}>

const isDefault = (selection: ColumnSelection): boolean =>
  selection.selected === (selection.columnType !== 'optional')

@Component({
  imports: [MatFormFieldModule, MatInputModule, FormsModule, IdrColumnsSelectorGridComponent, SearchFieldComponent],
  selector: 'app-idr-columns-selector',
  template: `
    <div class='root-container'>
      <div class='header'>
        <app-search-field
            placeholder="Find column"
            [(filterStringModel)]="filterStringModel"
        />
      </div>
      <div class='status'>
        <span>{{ getStatusMessage() }}</span>
        <a
            href='#'
            (click)="resetToDefaults($event)"
            [class.d-none]="isDefaultSelected()"
        >
          Restore Defaults
        </a>
      </div>
      <div class='grid-container'>
        <app-idr-columns-selector-grid
            [selections]="filteredSelections()"
            (selectionsChange)="onSelectionsChange($event)"
        />
      </div>
    </div>
  `,
  styles: `
    :host ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      height: 0;
    }
    
    .root-container {
      display: flex;
      flex-direction: column;
      height: calc(100vh - 200px);;
    }
    
    .grid-container {
      flex-basis: 0;
      flex-grow: 1;
    }
    
    .header {
      display: flex;
      justify-content: space-between;

      app-search-field {
        width: 100%;
      }
    }
    .status {
      margin-top: 1rem;
      margin-bottom: 1rem;
      font-size: 16px;
      display: flex;
      justify-content: space-between;
      span {
        flex-basis: 0;
        flex-grow: 1;
        padding-right: 5px;
      }
      span, a {
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      a {
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  `,
})
export class IdrColumnsSelectorComponent {
  readonly selections = input.required<ColumnSelection[]>()

  readonly selectionsChange = output<ColumnSelection[]>()

  protected selectedCount = computed(() =>
    sumBy(this.selections(), _ => _.selected ? 1 : 0),
  )

  protected isDefaultSelected = computed(() =>
    this.selections().every(selection => isDefault(selection)),
  )

  protected filterStringModel = model('')
  protected filterString = computed(() => this.filterStringModel().trim().toLowerCase())

  protected filteredSelections = computed<ColumnSelection[]>(() => {
    const selections = this.selections()
    const filter = this.filterString()
    if (filter === '') {
      return selections
    } else {
      return selections.filter(d => d.label.toLowerCase().includes(filter))
    }
  })

  protected isColumnsFiltered(): boolean {
    return this.filterString().length > 0
  }

  protected getStatusMessage() {
    const getFilterMessage = () => {
      if (this.isColumnsFiltered()) {
        const columnsFound = this.filteredSelections().length
        if (columnsFound === 0) {
          return 'All selections filtered'
        } else {
          return `${columnsFound} of ${this.selections().length} selections`
        }
      } else {
        return `${this.selections().length} selections`
      }
    }

    return this.selections().length === 0 ? '' : `${getFilterMessage()}, ${this.selectedCount()} selected`
  }

  protected onSelectionsChange(updates: readonly ColumnSelection[]) {
    const byId: Record<string, ColumnSelection> = {}
    updates.forEach(selection => {
      byId[selection.id] = selection
    })

    this.selectionsChange.emit(this.selections().map(s => byId[s.id] ?? s))
  }

  protected resetToDefaults (event: Event) {
    event.preventDefault()
    this.selectionsChange.emit(this.selections().map(s => {
      const selected = s.columnType !== 'optional'
      return s.selected === selected ? s : { ...s, selected }
    }))
  }
}
